import { llmCourtCasesOpinionsSummary } from '@ltvco/refresh-lib/api';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useServiceToken } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useContext } from 'react';

import { type DisplayOpinion } from '../../types';

import { DEFAULT_QUERY_OPTIONS } from './useBaseCourtApi';

/**
 * Custom hook for fetching court case opinion summary using LLM
 * @param opinion - The opinion to summarize
 * @param enabled - Whether the query should be enabled
 * @returns Query result with summary data and loading state
 */
export const useCourtCaseOpinionsSummary = (
  opinion: DisplayOpinion | null,
  enabled: boolean = false
) => {
  const { data: tokenData, isLoading: isTokenLoading } =
    useServiceToken('oram');
  const { logError } = useContext(AppConfig);

  const opinionWithContent = opinion as {
    plain_text?: string;
    html_with_citations?: string;
  } | null;
  const opinionText =
    opinionWithContent?.plain_text ||
    opinionWithContent?.html_with_citations ||
    '';
  const opinionId = opinion?.id;

  return useQuery({
    queryKey: ['courtCasesOpinionsSummary', opinionId],
    queryFn: async () => {
      if (!tokenData?.token) {
        const error = new Error('Token not available');
        logError(
          'Failed to generate opinion summary: Token not available',
          error
        );
        throw error;
      }
      const params = { document: opinionText };
      return await llmCourtCasesOpinionsSummary.createCourtCasesOpinionsSummary(
        tokenData.token,
        params
      );
    },
    enabled:
      enabled &&
      Boolean(opinionText) &&
      Boolean(tokenData?.token) &&
      !isTokenLoading,
    ...DEFAULT_QUERY_OPTIONS,
  });
};
